import { useDispatch, useSelector } from "react-redux";

import { FACILITY_LABEL_LAYER, FACILITY_LAYER } from "constants/mapLayers.constants";
import { setActiveEntities, setIsTogglingMidstream } from "store/features";
import { RootState } from "store/rootReducer";

import useFeatureFlags from "hooks/useFeatureFlags";

import { EntityKind } from "models/entityKind";

import { useCheckedLayersChangeHandler } from "components/project/layers/hooks";
import { useProjectContext } from "components/project/projects/context";

/**
 * Custom hook for automatically adding facility entities when:
 * 1. Midstream widgets are present in the dashboard, OR
 * 2. Workspace title is "Midstream"
 * Facilities are only enabled if not disabled via session storage flags.
 */
export const SESSION_STORAGE_GLOBAL_FACILITY_TOGGLE = "global-disabled-facility";

export function useAutoEnableMidstream() {
  const dispatch = useDispatch();
  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);
  const projectContext = useProjectContext();
  const { handleCheckedLayersChange } = useCheckedLayersChangeHandler();
  const { hasFeature } = useFeatureFlags();

  const update = (capabilities, workspaceTitle) => {
    // EVA-10: "Base Midstream" is a hard prerequisite for all midstream
    // functionality. The "Midstream" workspace itself disappears server-side
    // when the flag is off, but a dashboard with a midstream widget could
    // still exist outside that workspace — this is the remaining
    // client-side entry point that could otherwise flip the user into
    // Facility entity-kind mode.
    if (!hasFeature("Base Midstream")) return;

    const isTitleMidstream = workspaceTitle === "Midstream";

    if (!capabilities?.hasMidstreamWidget && !isTitleMidstream) return;

    const isFacilitiesDisabledGlobal = sessionStorage.getItem(
      SESSION_STORAGE_GLOBAL_FACILITY_TOGGLE
    );

    const isUserNotDisablingMidstream =
      !isFacilitiesDisabledGlobal && !activeEntities.includes(EntityKind.Facility);

    const shouldEnableMidstream =
      (capabilities?.hasMidstreamWidget && isUserNotDisablingMidstream) ||
      (isTitleMidstream && isUserNotDisablingMidstream);

    if (shouldEnableMidstream) {
      dispatch(setActiveEntities([...activeEntities, EntityKind.Facility]));

      // Add facility layers to checked layer keys
      let checkedLayerKeys = [...projectContext.checkedLayerKeys];

      if (!checkedLayerKeys.includes(FACILITY_LAYER)) {
        checkedLayerKeys = [...checkedLayerKeys, FACILITY_LAYER];
      }
      if (!checkedLayerKeys.includes(FACILITY_LABEL_LAYER)) {
        checkedLayerKeys = [...checkedLayerKeys, FACILITY_LABEL_LAYER];
      }

      handleCheckedLayersChange({
        checkedLayerKeys: checkedLayerKeys,
        checkedTreeKeys: checkedLayerKeys
      });

      dispatch(setIsTogglingMidstream(false));
    }
  };

  return { update };
}
